import config, { convergeLogger } from '@converge/config';
import { useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { useAuthContext } from './require-auth';

/**
 * Enforces an inactivity (idle) session timeout on the web app.
 *
 * After `config.userIdlesAfterMinutes` of no user interaction (mouse,
 * keyboard, touch, scroll), the user's session is cleared via `resetUser()`
 * — which removes the stored tokens (localStorage) / triggers SSO logout and
 * sets the user to null, so `RequireAuth` redirects to the login page. The
 * `isUserIdle` flag is also set so the silent token-refresh loop stops
 * re-minting tokens (see `useAuthTokenRefresh`), preventing the session from
 * being kept alive indefinitely while the tab sits idle.
 *
 * `crossTab` keeps idleness in sync across tabs so activity in one tab keeps
 * every tab logged in, and going idle logs them all out together.
 *
 * Addresses pen-test finding WEB-L5 (excessive session timeout). The window is
 * currently 1 hour across all environments (`config.userIdlesAfterMinutes`).
 */
export const useIdleLogout = () => {
  const { user, resetUser, isUserIdle, setIsUserIdle } = useAuthContext();

  const onIdle = useCallback(() => {
    // Only act when there is an authenticated user to log out.
    if (!user) {
      return;
    }
    convergeLogger.log(
      `User idle for ${config.userIdlesAfterMinutes} minutes; logging out.`
    );
    setIsUserIdle(true);
    resetUser();
  }, [user, resetUser, setIsUserIdle]);

  const onActive = useCallback(() => {
    // Clear the idle flag when the user returns so the refresh loop resumes.
    if (isUserIdle) {
      setIsUserIdle(false);
    }
  }, [isUserIdle, setIsUserIdle]);

  useIdleTimer({
    timeout: config.userIdlesAfterMilliseconds,
    onIdle,
    onActive,
    // Only track activity while a user is signed in.
    startManually: false,
    stopOnIdle: false,
    crossTab: true,
    events: [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'wheel',
      'scroll',
    ],
  });

  return;
};
